import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface MobileNavItem {
  icon: string;
  label: string;
  tkey?: string;
  to: string;
  badge?: number;
  active?: boolean;
}

interface Props {
  items: MobileNavItem[];
  /** Ouvre le tiroir complet (toutes les pages). */
  onMore: () => void;
  moreLabel?: string;
  /** Bouton QR central (afficher / scanner) — réservé au commerce. */
  onQr?: () => void;
  /** Masque la barre (par ex. quand une modale plein écran est ouverte). */
  hidden?: boolean;
}

/**
 * MobileBottomNav — barre de navigation fixe en bas de l'écran (mobile uniquement).
 * Garantit la navigation même quand le tiroir latéral n'est pas utilisable.
 */
export default function MobileBottomNav({ items, onMore, moreLabel = 'Plus', onQr, hidden }: Props) {
  const { t } = useTranslation();
  if (hidden) return null;

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/95 backdrop-blur-md border-t border-background-200/70"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Navigation principale"
    >
      <div className="flex items-stretch justify-around h-14 w-full overflow-hidden">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            className={`relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium transition-colors cursor-pointer no-underline ${
              item.active ? 'text-primary-600' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {item.active && <span className="absolute top-0 h-0.5 w-6 rounded-full bg-primary-500"></span>}
            <span className="relative w-6 h-6 flex items-center justify-center">
              <i className={`${item.icon} text-[18px]`}></i>
              {item.badge && item.badge > 0 ? (
                <span className="absolute -top-1 -right-1.5 min-w-[15px] h-[15px] px-1 flex items-center justify-center rounded-full bg-primary-500 text-white text-[9px] font-semibold leading-none">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </span>
            <span className="block w-full text-center leading-none truncate">{t(item.tkey || item.label, { defaultValue: item.label })}</span>
          </Link>
        ))}

        {onQr && (
          <button
            type="button"
            onClick={onQr}
            aria-label="Mon QR Zifek"
            className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-qr-scan-2-line text-[18px]"></i>
            </span>
            <span className="block w-full text-center leading-none truncate">QR</span>
          </button>
        )}

        <button
          type="button"
          onClick={onMore}
          aria-label={moreLabel}
          className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
        >
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-menu-2-line text-[18px]"></i>
          </span>
          <span className="block w-full text-center leading-none truncate">{moreLabel}</span>
        </button>
      </div>
    </nav>
  );
}